import {
  DataType,
  type SettingTypeMetadata,
  type SettingTypeValueMap,
} from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createSettingsParam,
  createSettingsParams,
  ISettingsParam,
  SettingsParamType,
} from '@wix/tpa-settings';
import {
  BOOKINGS_MAIN_PAGE_EDITOR_X_PRESET_ID,
  BOOKINGS_MAIN_PAGE_PRESET_ID,
  CLASSIC_EDITOR_X_PRESET_ID,
  CLASSIC_PRESET_ID,
  GRID_EDITOR_X_PRESET_ID,
  GRID_PRESET_ID,
  SINGLE_SERVICE_EDITOR_X_PRESET_ID,
  SINGLE_SERVICE_PRESET_ID,
  STRIP_EDITOR_X_PRESET_ID,
  STRIP_PRESET_ID,
} from '../../consts';
import {
  AlignmentOptions,
  ButtonStyleOptions,
  DisplayServicesByOptions,
  DockLocationOptions,
  FilterLayoutOptions,
  FilterServicesByOptions,
  ImagePositionOptions,
  ImageResizeOptions,
  ImageShapeOptions,
  ImageSizeOptions,
  MigrationStatus,
  ClickTarget,
  CourseClickTarget,
  ServiceListLayoutOptions,
} from '../../types/types';
import { AccessibilityHtmlTags } from '../../utils/settings/htmlTags';
import {
  getIsServiceImageVisibleDefaultValue,
  getServiceImageShapeDefaultValue,
  getServiceListLayoutDefaultValue,
  getTextAlignmentDefaultValue,
} from './settingsParams-utils';

export enum ISettingsParamsBooleansKeys {
  flipImageAndTextRatio = 'flipImageAndTextRatio', // IMAGE_AND_TEXT_RATIO_IS_FLIPPED
  isTitleVisible = 'isTitleVisible', // DISPLAY_MULTI_OFFERINGS_TITLE
  isListFilterVisible = 'isListFilterVisible', // DISPLAY_CATEGORIES
  isServiceImageVisible = 'isServiceImageVisible', // DISPLAY_IMAGE, MOBILE_DISPLAY_IMAGE
  isListFilterStretched = 'isListFilterStretched', // FIT_CATEGORY_WIDTH
  isServicesDividerVisible = 'isServicesDividerVisible', // DISPLAY_SERVICE_DIVIDER, MOBILE_SERVICE_DIVIDER
  isOnlineBadgeVisible = 'isOnlineBadgeVisible', // DISPLAY_ONLINE_INDICATION, MOBILE_DISPLAY_ONLINE_INDICATION
  isTagLineVisible = 'isTagLineVisible', // DISPLAY_TAG_LINE, MOBILE_DISPLAY_TAG_LINE
  isMoreInfoButtonVisible = 'isMoreInfoButtonVisible', // DISPLAY_MORE_INFO_LABEL, MOBILE_DISPLAY_MORE_INFO_LABEL
  isServiceDividerVisible = 'isServiceDividerVisible', // DISPLAY_DIVIDER, MOBILE_DISPLAY_DIVIDER
  isServiceOfferedDaysVisible = 'isServiceOfferedDaysVisible', // DISPLAY_DAYS_OFFERED, MOBILE_DISPLAY_DAYS_OFFERED
  isServiceStartDateVisible = 'isServiceStartDateVisible', // DISPLAY_START_DATE, MOBILE_DISPLAY_START_DATE
  isCourseAvailabilityVisible = 'isCourseAvailabilityVisible', // DISPLAY_COURSE_AVAILABILITY
  displayNumberOfSpots = 'displayNumberOfSpots', // DISPLAY_NUMBER_OF_SPOTS
  isServiceDurationVisible = 'isServiceDurationVisible', // DISPLAY_DURATION, MOBILE_DISPLAY_DURATION
  isServicePriceVisible = 'isServicePriceVisible', // DISPLAY_PRICE, MOBILE_DISPLAY_PRICE
  isPriceAfterDiscountVisible = 'isPriceAfterDiscountVisible', // DISPLAY_PRICE_AFTER_DISCOUNT
  isDiscountNameVisible = 'isDiscountNameVisible', // DISPLAY_DISCOUNT_NAME
  isBookButtonVisible = 'isBookButtonVisible', // DISPLAY_BUTTON, MOBILE_DISPLAY_BUTTON
  isExplorePlansVisible = 'isExplorePlansVisible', // DISPLAY_EXPLORE_PLANS, MOBILE_DISPLAY_EXPLORE_PLANS
  showAllServicesFilterOption = 'showAllServicesFilterOption', // CATEGORY_ALL_SERVICES_SHOW
  alternateImageInfoSides = 'alternateImageInfoSides', // CATEGORY_ALL_SERVICES_SHOW
  isMenuVisible = 'isMenuVisible', // DISPLAY_MENU
}

export enum ISettingsParamsNumbersKeys {
  cardsPerRow = 'cardsPerRow', // LAYOUT_CARDS_PER_ROW
  cardSpacing = 'cardSpacing', // SPACE_BETWEEN_OFFERINGS
  gridCardsSpacing = 'gridCardsSpacing', // CARDS_SPACING, MOBILE_CARDS_SPACING
  servicesDividerWidth = 'servicesDividerWidth', // SERVICE_DIVIDER_WIDTH
  stripCardSpacing = 'stripCardSpacing', // STRIP_SPACE_BETWEEN_OFFERINGS
}

export enum ISettingsParamsTextKeys {
  allServices = 'allServices', // CATEGORY_ALL_SERVICES_TEXT
  bookButtonText = 'bookButtonText', // BOOK_FLOW_ACTION_TEXT
  explorePlansText = 'explorePlansText', // BOOK_FLOW_ACTION_TEXT
  viewCourseButtonText = 'viewCourseButtonText', // VIEW_COURSE_BUTTON_TEXT
  moreInfoButtonText = 'moreInfoButtonText', // MORE_INFO_LABEL_TEXT
  noBookFlowText = 'noBookFlowText', // NO_BOOK_FLOW_ACTION_TEXT
  onlineBadgeText = 'onlineBadgeText', // ONLINE_INDICATION_TEXT
  pendingApprovalText = 'pendingApprovalText', // PENDING_APPROVAL_FLOW_ACTION_TEXT
  titleText = 'titleText', // MULTI_OFFERINGS_TITLE_TEXT
  courseAvailabilityText = 'courseAvailabilityText', // COURSE_AVAILABILITY_TEXT
  courseAvailableText = 'courseAvailableText', // COURSE_AVAILABLE_TEXT
  courseNoAvailabilityText = 'courseNoAvailabilityText', // COURSE_NO_AVAILABILITY_TEXT,
  loadMoreButtonText = 'loadMoreButtonText', // LOAD_MORE_BUTTON_TEXT
  loadPreviousButtonText = 'loadPreviousButtonText', // LOAD_PREVIOUS_BUTTON_TEXT
}

export enum ISettingsParamsCustomsKeys {
  bookButtonStyle = 'bookButtonStyle', // BUTTON_STYLE
  filterLayout = 'filterLayout', // CATEGORY_LAYOUT_OPTION
  filterServicesBy = 'filterServicesBy', // CATEGORIES_TYPE
  displayServicesBy = 'displayServicesBy', // FILTER_BY
  listFilterAlignment = 'listFilterAlignment', // CATEGORY_ALIGNMENT
  textAlignment = 'textAlignment', // TEXT_ALIGNMENT, MOBILE_TEXT_ALIGNMENT
  titleAlignment = 'titleAlignment', // MULTI_OFFERINGS_TITLE_ALIGNMENT
  serviceListLayout = 'serviceListLayout', // OFFERINGS_LIST_LAYOUT, MOBILE_OFFERING_LIST_LAYOUT
  serviceImagePosition = 'serviceImagePosition', // IMAGE_POSITION_OPTION
  serviceImageShape = 'serviceImageShape', // IMAGE_SHAPE_OPTION
  serviceImageResize = 'serviceImageResize', // IMAGE_RESIZE_OPTION, MOBILE_IMAGE_RESIZE_OPTION
  serviceImageDockLocation = 'serviceImageDockLocation', // PIN_LOCATION, MOBILE_PIN_LOCATION
  serviceClickTarget = 'serviceClickTarget', // SERVICE_CLICK_TARGET
  bookButtonClickTarget = 'bookButtonClickTarget', // BOOK_BUTTON_CLICK_TARGET
  courseServiceClickTarget = 'courseServiceClickTarget', // COURSE_SERVICE_CLICK_TARGET
  courseBookButtonClickTarget = 'courseBookButtonClickTarget', // COURSE_BOOK_BUTTON_CLICK_TARGET
}

type ISettingsParamsBooleans = {
  [key in `${ISettingsParamsBooleansKeys}`]: SettingsParamType.Boolean;
};

type ISettingsParamsNumbers = {
  [key in `${ISettingsParamsNumbersKeys}`]: SettingsParamType.Number;
};

type ISettingsParamsText = {
  [key in `${ISettingsParamsTextKeys}`]: SettingsParamType.Text;
};

type ISettingsParamsCustom = {
  [key in `${ISettingsParamsCustomsKeys}`]:
    | ButtonStyleOptions
    | FilterLayoutOptions
    | FilterServicesByOptions
    | DisplayServicesByOptions
    | AlignmentOptions
    | ServiceListLayoutOptions
    | ImagePositionOptions
    | ImageShapeOptions
    | ImageResizeOptions
    | ClickTarget
    | CourseClickTarget
    | DockLocationOptions;
};

export type ISettingsParams = {
  presetId: SettingsParamType.String;
  selectedLocations: string[]; // SELECTED_LOCATIONS
  bookButtonStyle: ButtonStyleOptions; // BUTTON_STYLE
  filterLayout: FilterLayoutOptions; // CATEGORY_LAYOUT_OPTION
  listFilterAlignment: AlignmentOptions; // CATEGORY_ALIGNMENT
  textAlignment: AlignmentOptions; // TEXT_ALIGNMENT, MOBILE_TEXT_ALIGNMENT
  filterServicesBy: FilterServicesByOptions; // CATEGORIES_TYPE
  displayServicesBy: DisplayServicesByOptions; // FILTER_BY
  titleAlignment: AlignmentOptions; // MULTI_OFFERINGS_TITLE_ALIGNMENT
  serviceListLayout: ServiceListLayoutOptions; // OFFERING_LIST_LAYOUT, MOBILE_OFFERING_LIST_LAYOUT
  serviceImagePosition: ImagePositionOptions; // IMAGE_POSITION_OPTION
  serviceImageShape: ImageShapeOptions; // IMAGE_SHAPE_OPTION
  serviceImageSize: ImageSizeOptions; // IMAGE_SIZE_OPTION, MOBILE_IMAGE_SIZE_OPTION
  serviceImageResize: ImageResizeOptions; // IMAGE_RESIZE_OPTION, MOBILE_IMAGE_RESIZE_OPTION
  serviceImageDockLocation: DockLocationOptions; // PIN_LOCATION, MOBILE_PIN_LOCATION
  selectedCategories: string[];
  selectedServices: string[];
  selectedService: SettingsParamType.String;
  widgetTitleFontHtmlTag: SettingsParamType.String; // MULTI_OFFERINGS_TITLE_FONT.htmlTag
  widgetMenuTitleFontHtmlTag: SettingsParamType.String; // CATEGORY_NAME_FONT.htmlTag
  serviceNameFontHtmlTag: SettingsParamType.String; // OFFERING_NAME_FONT.htmlTag
  courseAvailabilityFontHtmlTag: SettingsParamType.String; // COURSE_AVAILABILITY_FONT.htmlTag
  serviceTagLineFontHtmlTag: SettingsParamType.String; // OFFERING_TAGLINE_FONT.htmlTag
  moreInfoButtonFontHtmlTag: SettingsParamType.String; // OFFERING_MORE_INFO_LABEL_FONT.htmlTag
  serviceDetailsFontHtmlTag: SettingsParamType.String; // OFFERING_DETAILS_FONT.htmlTag
  migrationStatus: MigrationStatus;
  servicesPerPage: SettingsParamType.Number;
} & ISettingsParamsBooleans &
  ISettingsParamsNumbers &
  ISettingsParamsText &
  ISettingsParamsCustom;

const presetId = createSettingsParam('presetId', {
  getDefaultValue: () => '',
});

export const uninitializedVisibilitySettingsFalseValue = -1 as any as boolean;
export const uninitializedVisibilitySettingsTrueValue = -2 as any as boolean;

const isUninitializedVisibilitySettingsValue = (value: boolean) =>
  value === uninitializedVisibilitySettingsFalseValue ||
  value === uninitializedVisibilitySettingsTrueValue;

const getBooleanMetadataType = (defaultValue: boolean) =>
  isUninitializedVisibilitySettingsValue(defaultValue)
    ? DataType.UNKNOWN_DataType
    : DataType.booleanValue;

const getMetadata = <T extends DataType>(
  type: keyof SettingTypeValueMap,
  defaultValue: T,
): SettingTypeMetadata => ({
  type: type as DataType.UNKNOWN_DataType, // type assertion is needed to avoid strange TypeScript error
  defaultValue: {
    type: 'static',
    value: defaultValue,
  },
});

const getMetadataWithMobileDefault = <T extends DataType>(
  type: T,
  defaultValue: SettingTypeValueMap[T]['value'],
  mobileDefaultValue: SettingTypeValueMap[T]['value'],
): SettingTypeMetadata => ({
  type: type as DataType.UNKNOWN_DataType, // type assertion is needed to avoid strange TypeScript error
  defaultValue: {
    type: 'static',
    value: defaultValue,
  },
  mobileDefaultValue: {
    type: 'static',
    value: mobileDefaultValue,
  },
});

const settingsParam = <T extends DataType>(
  type: keyof SettingTypeValueMap,
  defaultValue: SettingTypeValueMap[T],
) => ({
  getDefaultValue: () => defaultValue,
  metadata: getMetadata(type, defaultValue),
});

const typedSettingsParam = <Type extends SettingsParamType, T extends DataType>(
  dataType: keyof SettingTypeValueMap,
  type: Type | undefined,
  defaultValue: SettingTypeValueMap[T],
) => ({
  type,
  ...settingsParam(dataType, defaultValue),
});

const settingsParamWithMobileDefault = <T extends DataType>(
  type: T,
  defaultValue: SettingTypeValueMap[T]['value'],
  mobileDefaultValue: SettingTypeValueMap[T]['value'],
  inheritDesktop?: boolean,
): Omit<ISettingsParam, 'key'> => ({
  getDefaultValue: ({ isMobile }) =>
    isMobile ? mobileDefaultValue : defaultValue,
  metadata: getMetadataWithMobileDefault(
    type,
    defaultValue,
    mobileDefaultValue,
  ),
  inheritDesktop,
});

const booleanSettingsParam = (defaultValue: boolean) =>
  typedSettingsParam(
    getBooleanMetadataType(defaultValue),
    SettingsParamType.Boolean,
    defaultValue,
  );

const booleanMetadata = (defaultValue: boolean): SettingTypeMetadata => ({
  type: getBooleanMetadataType(defaultValue) as DataType.UNKNOWN_DataType,
  defaultValue: {
    type: 'static',
    value: defaultValue,
  },
});

const textEnumMetadata = <T extends string>(
  defaultValue: T,
): SettingTypeMetadata => ({
  type: DataType.textEnum,
  defaultValue: {
    type: 'static',
    value: defaultValue,
  },
});

const stringSettingsParam = (defaultValue: string) =>
  typedSettingsParam(DataType.text, SettingsParamType.String, defaultValue);

const textSettingsParam = (
  defaultValueKey: string,
): Omit<ISettingsParam<SettingsParamType.Text>, 'key'> => ({
  type: SettingsParamType.Text,
  getDefaultValue: ({ t }) => t(defaultValueKey),
  metadata: ({ t }) => ({
    type: DataType.text,
    defaultValue: {
      type: 'static',
      value: t(defaultValueKey),
    },
  }),
});

const isBookButtonVisible = createSettingsParam(
  'isBookButtonVisible',
  booleanSettingsParam(uninitializedVisibilitySettingsTrueValue),
);

export default createSettingsParams<ISettingsParams>({
  migrationStatus: settingsParam(
    DataType.textEnum,
    MigrationStatus.NOT_MIGRATED,
  ),
  presetId: presetId as any,
  serviceListLayout: {
    inheritDesktop: false,
    getDefaultValue: ({
      isMobile,
      getSettingParamValue,
    }): ServiceListLayoutOptions => {
      const preset = getSettingParamValue(presetId);
      return getServiceListLayoutDefaultValue(preset, isMobile);
    },
    metadata: textEnumMetadata(ServiceListLayoutOptions.GRID),
  },
  titleText: textSettingsParam('multi-offerings.title'),
  titleAlignment: settingsParam(DataType.textEnum, AlignmentOptions.CENTER),
  listFilterAlignment: settingsParam(
    DataType.textEnum,
    AlignmentOptions.CENTER,
  ),
  textAlignment: {
    getDefaultValue: ({ getSettingParamValue, isMobile }): AlignmentOptions => {
      const preset = getSettingParamValue(presetId);
      return getTextAlignmentDefaultValue(preset, isMobile);
    },
    metadata: textEnumMetadata(AlignmentOptions.LEFT),
  },
  filterLayout: settingsParamWithMobileDefault(
    DataType.textEnum,
    FilterLayoutOptions.TABS,
    FilterLayoutOptions.DROPDOWN,
    false,
  ),
  filterServicesBy: settingsParam(
    DataType.textEnum,
    FilterServicesByOptions.CATEGORIES,
  ),
  displayServicesBy: settingsParam(
    DataType.textEnum,
    DisplayServicesByOptions.BY_SERVICES,
  ),
  isListFilterStretched: booleanSettingsParam(false),
  allServices: textSettingsParam('category-name.all-services'),
  showAllServicesFilterOption: booleanSettingsParam(true),
  alternateImageInfoSides: booleanSettingsParam(false),
  serviceImagePosition: settingsParam(
    DataType.textEnum,
    ImagePositionOptions.LEFT,
  ),
  isServiceImageVisible: {
    type: SettingsParamType.Boolean,
    getDefaultValue: ({ getSettingParamValue, isMobile }): boolean => {
      const preset = getSettingParamValue(presetId);
      return getIsServiceImageVisibleDefaultValue(preset, isMobile)
        ? uninitializedVisibilitySettingsTrueValue
        : uninitializedVisibilitySettingsFalseValue;
    },
    metadata: booleanMetadata(uninitializedVisibilitySettingsTrueValue),
  },
  isTitleVisible: {
    type: SettingsParamType.Boolean,
    getDefaultValue: ({ getSettingParamValue }): boolean => {
      const preset = getSettingParamValue(presetId);
      switch (preset) {
        case BOOKINGS_MAIN_PAGE_PRESET_ID:
        case BOOKINGS_MAIN_PAGE_EDITOR_X_PRESET_ID:
          return uninitializedVisibilitySettingsTrueValue;
        default:
          return uninitializedVisibilitySettingsFalseValue;
      }
    },
    metadata: booleanMetadata(uninitializedVisibilitySettingsTrueValue),
  },
  isListFilterVisible: {
    type: SettingsParamType.Boolean,
    getDefaultValue: ({ getSettingParamValue }): boolean => {
      const preset = getSettingParamValue(presetId);
      switch (preset) {
        case BOOKINGS_MAIN_PAGE_PRESET_ID:
        case BOOKINGS_MAIN_PAGE_EDITOR_X_PRESET_ID:
          return true;
        default:
          return false;
      }
    },
    metadata: booleanMetadata(true),
  },
  serviceImageShape: {
    getDefaultValue: ({
      isMobile,
      getSettingParamValue,
    }): ImageShapeOptions => {
      const preset = getSettingParamValue(presetId);
      return getServiceImageShapeDefaultValue(preset, isMobile);
    },
    metadata: textEnumMetadata(ImageShapeOptions.RECTANGLE),
  },
  serviceImageSize: settingsParamWithMobileDefault(
    DataType.textEnum,
    ImageSizeOptions.SMALL,
    ImageSizeOptions.MEDIUM,
    false,
  ),
  serviceImageResize: {
    inheritDesktop: false,
    ...settingsParam(DataType.textEnum, ImageResizeOptions.CROP),
  },
  serviceImageDockLocation: {
    inheritDesktop: false,
    ...settingsParam(DataType.textEnum, DockLocationOptions.MIDDLE),
  },
  serviceClickTarget: settingsParam(
    DataType.textEnum,
    ClickTarget.BOOKING_CALENDAR,
  ),
  courseServiceClickTarget: settingsParam(
    DataType.textEnum,
    CourseClickTarget.SERVICE_PAGE,
  ),
  selectedLocations: typedSettingsParam(
    DataType.container,
    SettingsParamType.Object,
    [],
  ),
  flipImageAndTextRatio: {
    type: SettingsParamType.Boolean,
    getDefaultValue: ({ getSettingParamValue }): boolean => {
      const preset = getSettingParamValue(presetId);
      switch (preset) {
        case SINGLE_SERVICE_PRESET_ID:
        case SINGLE_SERVICE_EDITOR_X_PRESET_ID:
        case CLASSIC_PRESET_ID:
        case CLASSIC_EDITOR_X_PRESET_ID:
          return true;
        default:
          return false;
      }
    },
    metadata: booleanMetadata(false),
  },
  isServicesDividerVisible: booleanSettingsParam(true),
  servicesDividerWidth: typedSettingsParam(
    DataType.number,
    SettingsParamType.Number,
    1,
  ),
  cardsPerRow: settingsParamWithMobileDefault(DataType.number, 3, 1, false),
  cardSpacing: typedSettingsParam(
    DataType.number,
    SettingsParamType.Number,
    60,
  ),
  gridCardsSpacing: settingsParamWithMobileDefault(DataType.number, 32, 20),
  stripCardSpacing: typedSettingsParam(
    DataType.number,
    SettingsParamType.Number,
    0,
  ),
  isOnlineBadgeVisible: booleanSettingsParam(
    uninitializedVisibilitySettingsFalseValue,
  ),
  onlineBadgeText: textSettingsParam('info-card.design.online-indication'),
  courseAvailabilityText: stringSettingsParam(''),
  courseAvailableText: textSettingsParam(
    'service.schedule.course.available-spots',
  ),
  courseNoAvailabilityText: textSettingsParam(
    'service.schedule.course.fully-booked',
  ),
  isTagLineVisible: {
    type: SettingsParamType.Boolean,
    getDefaultValue: ({ getSettingParamValue, isMobile }): boolean => {
      if (isMobile) {
        return uninitializedVisibilitySettingsTrueValue;
      }
      const preset = getSettingParamValue(presetId);
      switch (preset) {
        case STRIP_PRESET_ID:
        case STRIP_EDITOR_X_PRESET_ID:
        case GRID_PRESET_ID:
        case GRID_EDITOR_X_PRESET_ID:
        case BOOKINGS_MAIN_PAGE_PRESET_ID:
        case BOOKINGS_MAIN_PAGE_EDITOR_X_PRESET_ID:
        case SINGLE_SERVICE_PRESET_ID:
        case SINGLE_SERVICE_EDITOR_X_PRESET_ID:
          return uninitializedVisibilitySettingsFalseValue;
        default:
          return uninitializedVisibilitySettingsTrueValue;
      }
    },
    metadata: {
      type: DataType.UNKNOWN_DataType,
      defaultValue: {
        type: 'static',
        value: uninitializedVisibilitySettingsFalseValue,
      },
      mobileDefaultValue: {
        type: 'static',
        value: uninitializedVisibilitySettingsTrueValue,
      },
    },
  },
  isMoreInfoButtonVisible: booleanSettingsParam(
    uninitializedVisibilitySettingsFalseValue,
  ),
  moreInfoButtonText: textSettingsParam('info-card.design.more-info-label'),
  isServiceDividerVisible: {
    type: SettingsParamType.Boolean,
    getDefaultValue: ({ getSettingParamValue, isMobile }): boolean => {
      if (isMobile) {
        return uninitializedVisibilitySettingsTrueValue;
      }
      const preset = getSettingParamValue(presetId);
      switch (preset) {
        case SINGLE_SERVICE_PRESET_ID:
        case SINGLE_SERVICE_EDITOR_X_PRESET_ID:
          return uninitializedVisibilitySettingsFalseValue;
        default:
          return uninitializedVisibilitySettingsTrueValue;
      }
    },
    metadata: booleanMetadata(uninitializedVisibilitySettingsTrueValue),
  },
  isServiceOfferedDaysVisible: {
    type: SettingsParamType.Boolean,
    getDefaultValue: ({ getSettingParamValue, isMobile }): boolean => {
      if (isMobile) {
        return uninitializedVisibilitySettingsTrueValue;
      }
      const preset = getSettingParamValue(presetId);
      switch (preset) {
        case STRIP_PRESET_ID:
        case STRIP_EDITOR_X_PRESET_ID:
          return uninitializedVisibilitySettingsFalseValue;
        default:
          return uninitializedVisibilitySettingsTrueValue;
      }
    },
    metadata: booleanMetadata(uninitializedVisibilitySettingsTrueValue),
  },
  isServiceStartDateVisible: {
    type: SettingsParamType.Boolean,
    getDefaultValue: ({ getSettingParamValue, isMobile }): boolean => {
      if (isMobile) {
        return uninitializedVisibilitySettingsTrueValue;
      }
      const preset = getSettingParamValue(presetId);
      switch (preset) {
        case STRIP_PRESET_ID:
        case STRIP_EDITOR_X_PRESET_ID:
          return uninitializedVisibilitySettingsFalseValue;
        default:
          return uninitializedVisibilitySettingsTrueValue;
      }
    },
    metadata: booleanMetadata(uninitializedVisibilitySettingsTrueValue),
  },
  isCourseAvailabilityVisible: booleanSettingsParam(true),
  displayNumberOfSpots: booleanSettingsParam(false),
  isServiceDurationVisible: {
    type: SettingsParamType.Boolean,
    getDefaultValue: ({ getSettingParamValue, isMobile }): boolean => {
      if (isMobile) {
        return uninitializedVisibilitySettingsTrueValue;
      }
      const preset = getSettingParamValue(presetId);
      switch (preset) {
        case CLASSIC_PRESET_ID:
        case CLASSIC_EDITOR_X_PRESET_ID:
        case STRIP_PRESET_ID:
        case STRIP_EDITOR_X_PRESET_ID:
          return uninitializedVisibilitySettingsFalseValue;
        default:
          return uninitializedVisibilitySettingsTrueValue;
      }
    },
    metadata: booleanMetadata(uninitializedVisibilitySettingsTrueValue),
  },
  isServicePriceVisible: booleanSettingsParam(
    uninitializedVisibilitySettingsTrueValue,
  ),
  isPriceAfterDiscountVisible: booleanSettingsParam(
    uninitializedVisibilitySettingsTrueValue,
  ),
  isDiscountNameVisible: booleanSettingsParam(
    uninitializedVisibilitySettingsTrueValue,
  ),
  isBookButtonVisible,
  isExplorePlansVisible: {
    type: SettingsParamType.Boolean,
    getDefaultValue: ({ getSettingParamValue }) =>
      getSettingParamValue(isBookButtonVisible),
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'ref',
        ref: 'isBookButtonVisible',
      },
    },
  },
  noBookFlowText: textSettingsParam('info-card.design.button.more-info'),
  pendingApprovalText: textSettingsParam(
    'info-card.design.button.pending-approval',
  ),
  bookButtonText: textSettingsParam('info-card.design.button.book-it'),
  loadMoreButtonText: textSettingsParam(
    'settings-desktop-editor.text.link.load-more-button.placeholder',
  ),
  loadPreviousButtonText: textSettingsParam(
    'settings-desktop-editor.text.link.load-previous-button.placeholder',
  ),
  servicesPerPage: typedSettingsParam(
    DataType.number,
    SettingsParamType.Number,
    100,
  ),
  explorePlansText: textSettingsParam('info-card.design.explore-plans'),
  bookButtonStyle: settingsParam(
    DataType.textEnum,
    ButtonStyleOptions.SQUARE_FILL,
  ),
  viewCourseButtonText: textSettingsParam(
    'info-card.design.button.view-course',
  ),
  selectedCategories: typedSettingsParam(
    DataType.container,
    SettingsParamType.Object,
    [],
  ),
  selectedServices: typedSettingsParam(
    DataType.container,
    SettingsParamType.Object,
    [],
  ),
  selectedService: stringSettingsParam(''),
  courseAvailabilityFontHtmlTag: stringSettingsParam(
    AccessibilityHtmlTags.Paragraph,
  ),
  widgetTitleFontHtmlTag: stringSettingsParam(
    AccessibilityHtmlTags.MainHeading,
  ),
  widgetMenuTitleFontHtmlTag: stringSettingsParam(
    AccessibilityHtmlTags.SecondaryHeading,
  ),
  serviceNameFontHtmlTag: stringSettingsParam(
    AccessibilityHtmlTags.SecondaryHeading,
  ),
  serviceTagLineFontHtmlTag: stringSettingsParam(
    AccessibilityHtmlTags.Paragraph,
  ),
  moreInfoButtonFontHtmlTag: stringSettingsParam(
    AccessibilityHtmlTags.Paragraph,
  ),
  serviceDetailsFontHtmlTag: stringSettingsParam(
    AccessibilityHtmlTags.Paragraph,
  ),
  isMenuVisible: booleanSettingsParam(false),
  bookButtonClickTarget: settingsParam(
    DataType.textEnum,
    ClickTarget.BOOKING_CALENDAR,
  ),
  courseBookButtonClickTarget: settingsParam(
    DataType.textEnum,
    CourseClickTarget.SERVICE_PAGE,
  ),
});
